import { useState } from 'react';
import { useAuth } from '@/hooks';
import { Card, Button } from '@medikei/ui';
import { User, Mail, Shield, Clock, Video, MapPin, Home, Plus } from 'lucide-react';
import {
  ExpressCard,
  NextAppointmentCard,
  QuickLinksCard,
} from '@/components/dashboard';
import { InAppSchedulingModal } from '@/components/scheduling';
import { ReferralCard } from '@/components/referral';
import { ActiveConsultationBanner } from '@/components/telemedicine/ActiveConsultationBanner';

export function DashboardPage() {
  const { user } = useAuth();

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getUserName = (): string => {
    if (!user) return 'Usuário';
    if (user.name) return user.name.split(' ')[0] ?? 'Usuário';
    if (user.email) return user.email.split('@')[0] ?? 'Usuário';
    return 'Usuário';
  };

  const roleLabel =
    user?.role === 'PATIENT'
      ? 'Paciente'
      : user?.role === 'DOCTOR'
      ? 'Médico'
      : user?.role === 'ADMIN'
      ? 'Administrador'
      : user?.role || 'Paciente';

  const statusLabel =
    user?.status === 'ACTIVE'
      ? 'Ativo'
      : user?.status === 'PENDING'
      ? 'Pendente'
      : user?.status === 'BLOCKED'
      ? 'Bloqueado'
      : user?.status || 'Ativo';

  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [schedulingModality, setSchedulingModality] = useState<'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR'>('TELEMEDICINA');

  const handleOpenScheduling = (modality: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR') => {
    setSchedulingModality(modality);
    setIsSchedulingModalOpen(true);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">
          {greeting()}, {getUserName()}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">
          Bem-vindo de volta! Aqui está o resumo geral da sua conta.
        </p>
      </div>

      {/* Banner de Reconexão para Teleconsulta Ativa */}
      {user?.role === 'PATIENT' && <ActiveConsultationBanner role="PATIENT" />}

      {/* Atalhos Rápidos para Marcar Consulta no Dashboard */}
      {user?.role === 'PATIENT' && (
        <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shrink-0 shadow-sm">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base text-foreground leading-snug">
                Marcar Nova Consulta
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Agendamento rápido no painel por telemedicina, presencial ou visita domiciliar
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleOpenScheduling('TELEMEDICINA')}
              className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
            >
              <Video className="w-3.5 h-3.5" />
              <span>+ Telemedicina</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenScheduling('PRESENCIAL')}
              className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-background border-blue-500/40 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 shadow-xs"
            >
              <MapPin className="w-3.5 h-3.5 text-blue-600" />
              <span>+ Consultório</span>
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenScheduling('DOMICILIAR')}
              className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-background border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 shadow-xs"
            >
              <Home className="w-3.5 h-3.5 text-emerald-600" />
              <span>+ Médico em Casa</span>
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <ExpressCard className="md:col-span-2" />
        <NextAppointmentCard className="md:col-span-2" />
      </div>

      <QuickLinksCard />

      <ReferralCard />

      <Card className="p-4 sm:p-6 rounded-2xl border border-border">
        <h2 className="text-lg sm:text-xl font-bold mb-4 text-foreground">Informações da Conta</h2>
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-center gap-3 p-3.5 sm:p-4 bg-muted/40 rounded-xl border border-border/40 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <User className="text-primary" size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-medium">ID do Usuário</p>
              <p className="font-mono text-xs sm:text-sm truncate text-foreground" title={user?.id}>
                {user?.id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3.5 sm:p-4 bg-muted/40 rounded-xl border border-border/40 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="text-primary" size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-medium">E-mail</p>
              <p className="font-semibold text-xs sm:text-sm truncate text-foreground" title={user?.email}>
                {user?.email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3.5 sm:p-4 bg-muted/40 rounded-xl border border-border/40 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Shield className="text-primary" size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-medium">Função</p>
              <p className="font-semibold text-xs sm:text-sm text-foreground">{roleLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3.5 sm:p-4 bg-muted/40 rounded-xl border border-border/40 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Clock className="text-primary" size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground font-medium">Status</p>
              <p className={`font-semibold text-xs sm:text-sm ${
                user?.status === 'ACTIVE'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'
              }`}>
                {statusLabel}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {user?.role === 'PATIENT' && (
        <InAppSchedulingModal
          isOpen={isSchedulingModalOpen}
          onClose={() => setIsSchedulingModalOpen(false)}
          initialModality={schedulingModality}
        />
      )}
    </div>
  );
}
